// © 2026 Camp Promethean, LLC. All Rights Reserved.

import { BrowserRouter as Router, Route, Routes, Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { FacilityProvider } from "./contexts/FacilityContext";
import { AgoraAuthProvider } from "./contexts/agora";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import "./App.css";
import { AppLayout } from "@/components/layouts/AppLayout";
import { captureSparkCode } from "@/lib/spark";

// Capture spark referral code from URL on app load (first-touch browser handoff; durable credit is stored with the lead)
captureSparkCode();
import { DashboardShell } from "@/components/layouts/DashboardShell";
import { ThemeProvider } from "@/components/theme/ThemeProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import { createBrowserRouter } from "react-router-dom";
import { FeedbackWidget } from "@/components/feedback/FeedbackWidget";
import { isWaitlistCurtainEnabled } from "@/lib/waitlistCurtain";
import { FloatingAssistant } from "@/components/assistant/FloatingAssistant";
import { AdminRouteGuard } from "@/components/agora/AdminRouteGuard";
import IntegrationsPage from "@/pages/profile/integrations/Index";
import SelectionsPage from "./pages/SelectionsPage";
import { Link } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from '@/components/ui/sonner';
import { GlobalGestureHandler } from './components/GlobalGestureHandler';
import React, { Suspense } from "react";
import { SuspenseEmberLoader } from "@/components/loading";
import AdminRoute from "@/components/AdminRoute";

/** Pre-partition consulting URLs live on under /agora/professionals. */
const LegacyAgoraRedirect = () => {
  const location = useLocation();
  return (
    <Navigate
      to={`${location.pathname.replace(/^\/agora/, "/agora/professionals")}${location.search}`}
      replace
    />
  );
};

// Lazy load major page components
const Login = React.lazy(() => import("./pages/Login"));
const SignUp = React.lazy(() => import("./pages/SignUp"));
const Index = React.lazy(() => import("./pages/Index"));
const WaitlistCurtain = React.lazy(() => import("./pages/WaitlistCurtain"));
const AuthCallback = React.lazy(() => import("./pages/AuthCallback"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const GettingStarted = React.lazy(() => import("./pages/GettingStarted"));
const Help = React.lazy(() => import("./pages/Help"));
const AdminTutorials = React.lazy(() => import("./pages/admin/AdminTutorials"));
const TeachPrometheus = React.lazy(() => import('./pages/admin/TeachPrometheus'));
const Welcome = React.lazy(() => import("./pages/Welcome"));
const DashboardHome = React.lazy(() => import("./pages/dashboard/DashboardHome"));
const Pricing = React.lazy(() => import("./pages/Pricing"));
const Solutions = React.lazy(() => import("./pages/Solutions"));
const About = React.lazy(() => import("./pages/About"));
const Contact = React.lazy(() => import("./pages/Contact"));
const LegalPrivacy = React.lazy(() => import("./pages/legal/LegalPrivacy"));
const LegalTerms = React.lazy(() => import("./pages/legal/LegalTerms"));
const Profile = React.lazy(() => import("./pages/Profile"));
const SubscriptionSuccess = React.lazy(() => import("./pages/SubscriptionSuccess"));
const ArcaLanding = React.lazy(() => import("./pages/toolbox/ArcaLanding"));
const AnimusLanding = React.lazy(() => import("./pages/toolbox/AnimusLanding"));
const CropsLanding = React.lazy(() => import("./pages/crops/CropsLanding"));
const CropDetail = React.lazy(() => import("./pages/crops/CropDetail"));
const Standup = React.lazy(() => import("./pages/crops/Standup"));
const MyTasks = React.lazy(() => import("./pages/crops/MyTasks"));
const SpaceTimeline = React.lazy(() => import("./pages/crops/SpaceTimeline"));
const CultivationDiary = React.lazy(() => import("./pages/crops/CultivationDiary"));
const AncoraLanding = React.lazy(() => import("./pages/ancora/AncoraLanding"));
const AncoraCourses = React.lazy(() => import("./pages/ancora/AncoraCourses"));
const AncoraPro = React.lazy(() => import("./pages/ancora/AncoraPro"));
const AncoraAtomView = React.lazy(() => import("./pages/ancora/AtomView"));
const AncoraThemeLibrary = React.lazy(() => import("./pages/ancora/ThemeLibrary"));
const StrainLibrary = React.lazy(() => import("./pages/toolbox/animus/StrainLibrary"));
const ShotSizeCalculator = React.lazy(() => import("./pages/toolbox/arca/ShotSizeCalculator"));
const UnitConverter = React.lazy(() => import("./pages/toolbox/arca/converter"));
const ChemicalCalculator = React.lazy(() => import("./pages/toolbox/arca/ChemicalCalculator"));
const RunoffManager = React.lazy(() => import("./pages/toolbox/arca/RunoffManager"));
const PhotothermalRatioCalculator = React.lazy(() => import("./pages/toolbox/arca/photothermal"));
const LightLabLayout = React.lazy(() => import("./pages/toolbox/arca/light-lab/LightLabLayout"));
const LightLabDliCoreTool = React.lazy(() => import("./pages/toolbox/arca/light-lab/subtools/DliCoreEngineTool"));
const LightLabRampTool = React.lazy(() => import("./pages/toolbox/arca/light-lab/subtools/DliRampPlannerTool"));
const LightLabSpectrumTool = React.lazy(() => import("./pages/toolbox/arca/light-lab/subtools/SpectrumVisualizerTool"));
const LightLabEnergyTool = React.lazy(() => import("./pages/toolbox/arca/light-lab/subtools/EnergyEfficiencyTool"));
const AgroChemLog = React.lazy(() => import("./pages/toolbox/arca/AgroChemLog"));
// ARCA SDS hub — product identify + shared catalog
const ArcaSdsHub = React.lazy(() => import("./pages/toolbox/arca/sds/SdsHub"));
const ArcaCatalogReview = React.lazy(() => import("./pages/toolbox/arca/sds/CatalogReview"));
const FacilitiesManager = React.lazy(() => import("./pages/profile/facilities/FacilitiesManager"));
const Prometheus = React.lazy(() => import("./pages/toolbox/prometheus/Prometheus"));
const Prometheus2 = React.lazy(() => import("./pages/prometheus-2/Prometheus2"));
const PrometheusAgent = React.lazy(() => import("./pages/toolbox/prometheus/PrometheusAgent"));
const Prometheus3 = React.lazy(() => import("./pages/prometheus-3/Prometheus3"));
const Prometheus4 = React.lazy(() => import("./pages/toolbox/prometheus/Prometheus4"));
const Targets = React.lazy(() => import("./pages/toolbox/arca/Targets"));
const Tanks = React.lazy(() => import("./pages/toolbox/arca/Tanks"));
const StockTanks = React.lazy(() => import("./pages/toolbox/arca/StockTanks"));
const BatchTanks = React.lazy(() => import("./pages/toolbox/arca/BatchTanks"));
const CreateBatchTank = React.lazy(() => import("./pages/toolbox/arca/components/batchTank/CreateBatchTank"));
const EditBatchTank = React.lazy(() => import("./pages/toolbox/arca/components/batchTank/EditBatchTank"));
const Fertilizers = React.lazy(() => import("./pages/toolbox/arca/Fertilizers"));
const Fertility = React.lazy(() => import("./pages/toolbox/arca/Fertility"));
const FertilityApplicationLog = React.lazy(() => import("./pages/toolbox/arca/FertilityApplicationLog"));
const BeneficialInsects = React.lazy(() => import("./pages/toolbox/arca/BeneficialInsects"));
const AroyaReadings = React.lazy(() => import("./pages/aroya/AroyaReadings"));
const AbacusLanding = React.lazy(() => import("./pages/toolbox/abacus/AbacusLanding"));
const YieldManager = React.lazy(() => import("./pages/toolbox/abacus/YieldManager"));
const PriceSettings = React.lazy(() => import("./pages/toolbox/abacus/PriceSettings"));
const Feedback = React.lazy(() => import("./pages/Feedback"));
const AdminFeedback = React.lazy(() => import("./pages/AdminFeedback"));
const Atlas = React.lazy(() => import("./pages/Atlas"));
const StrainArchive = React.lazy(() => import("./pages/toolbox/animus/StrainArchive"));
const Origins = React.lazy(() => import("./pages/toolbox/animus/Origins"));
const BreedingProjectDetail = React.lazy(() => import("./pages/toolbox/animus/BreedingProjectDetail"));
const Traits = React.lazy(() => import("./pages/toolbox/animus/Traits"));
const TraitProjectDetail = React.lazy(() => import("./pages/toolbox/animus/trait-project/TraitProjectDetail"));
const AnimusAnalytics = React.lazy(() => import("./pages/toolbox/animus/analytics/AnimusAnalytics"));
const IrrigationSimulator = React.lazy(() => import("./pages/toolbox/arca/IrrigationForecaster"));
const PPMAnalysis = React.lazy(() => import("./pages/toolbox/arca/PPMAnalysis"));
const PPMAnalysisLogger = React.lazy(() => import("./pages/toolbox/arca/PPMAnalysisLogger"));
const PPMAnalysisRecords = React.lazy(() => import("./pages/toolbox/arca/PPMAnalysisRecords"));
const YieldAnalytics = React.lazy(() => import("./pages/toolbox/abacus/YieldAnalytics"));
const QualityAssessmentPage = React.lazy(() => import("./pages/toolbox/abacus/QualityAssessmentPage").then(module => ({ default: module.QualityAssessmentPage })));
const QualityWeightingsSettingsPage = React.lazy(() => import("./pages/toolbox/abacus/settings/QualityWeightings"));
const YieldGradesSettingsPage = React.lazy(() => import("./pages/toolbox/abacus/settings/YieldGrades"));
const CourseView = React.lazy(() => import("./pages/ancora/components/CourseView"));
const LessonPlayer = React.lazy(() => import("./pages/ancora/components/LessonPlayer"));
const QuizComponent = React.lazy(() => import("./pages/ancora/components/QuizComponent"));
const QuizPlayer = React.lazy(() => import("./pages/ancora/components/quiz/QuizPlayer"));
const CertificationAward = React.lazy(() => import("@/components/CertificationAward"));
const ResumeBuilder = React.lazy(() => import("@/components/ResumeBuilder"));
const QuizComponentTest = React.lazy(() => import("./tests/QuizComponentTest"));
const QuizBuilder = React.lazy(() => import("@/pages/admin/QuizBuilder"));
const UserFeedbackDashboard = React.lazy(() => import("./app/admin/user-feedback/page"));
const AdminInsights = React.lazy(() => import("@/pages/admin/AdminInsights"));
const AdminForecasts = React.lazy(() => import("@/pages/admin/AdminForecasts"));
const AdminAssumptions = React.lazy(() => import("@/pages/admin/AdminAssumptions"));
const AdminGrowgenLeads = React.lazy(() => import("@/pages/admin/AdminGrowgenLeads"));
const AdminGrowGen = React.lazy(() => import("@/pages/admin/AdminGrowGen"));
const PlusAdvisingConsole = React.lazy(() => import("@/pages/admin/PlusAdvisingConsole"));
const AdminBlessings = React.lazy(() => import("@/pages/admin/AdminBlessings"));
const AdminUsage = React.lazy(() => import("@/pages/admin/AdminUsage"));
const AncoraStudio = React.lazy(() => import("@/pages/admin/AncoraStudio"));
const AdminWaitlist = React.lazy(() => import("@/pages/admin/AdminWaitlist"));
const AdminHome = React.lazy(() => import("@/pages/admin/AdminHome"));
const AdminOpsOverview = React.lazy(() => import("@/pages/admin/ops/AdminOpsOverview"));
const AdminOpsAccounts = React.lazy(() => import("@/pages/admin/ops/AdminOpsAccounts"));
const AdminOpsAccountDetail = React.lazy(() => import("@/pages/admin/ops/AdminOpsAccountDetail"));
const AdminOpsUsers = React.lazy(() => import("@/pages/admin/ops/AdminOpsUsers"));
const AdminOpsLifecycle = React.lazy(() => import("@/pages/admin/ops/AdminOpsLifecycle"));
const AdminOpsRevenue = React.lazy(() => import("@/pages/admin/ops/AdminOpsRevenue"));
const AdminOpsHealth = React.lazy(() => import("@/pages/admin/ops/AdminOpsHealth"));
const AdminOpsAudit = React.lazy(() => import("@/pages/admin/ops/AdminOpsAudit"));
const AdminOpsSpark = React.lazy(() => import("@/pages/admin/ops/AdminOpsSpark"));
const FilterManager = React.lazy(() => import("./pages/toolbox/arca/components/filters/FilterManager"));
const SparkDashboard = React.lazy(() => import("./pages/spark/SparkDashboard"));
const ClientsPortalIndex = React.lazy(() => import('./pages/clients/Index'));
const ClientsResources = React.lazy(() => import('./pages/clients/resources'));
const ClientsEducation = React.lazy(() => import('./pages/clients/education'));
const ClientsCommunication = React.lazy(() => import('./pages/clients/communication'));
const ClientsWorkspace = React.lazy(() => import('./pages/clients/workspace'));
const ClientsOrgSelect = React.lazy(() => import('./pages/clients/OrgSelect'));
const NotAuthorized = React.lazy(() => import('./pages/clients/NotAuthorized'));
const JoinFacility = React.lazy(() => import('./pages/facility/JoinFacility'));
const FacilityAdministration = React.lazy(() => import('./pages/facility/FacilityAdministration'));
const FacilitySettings = React.lazy(() => import('./pages/facility/FacilitySettings'));
const CreateFacility = React.lazy(() => import('./pages/facility/CreateFacility'));
const AurumLanding = React.lazy(() => import("./pages/toolbox/aurum/AurumLanding"));
const LogsEngine = React.lazy(() => import("./pages/toolbox/aurum/LogsEngine"));
const SOPVault = React.lazy(() => import("./pages/toolbox/aurum/SOPVault"));
const SDSInventory = React.lazy(() => import("./pages/toolbox/aurum/SDSInventory"));
const PurchaseArchive = React.lazy(() => import("./pages/toolbox/aurum/PurchaseArchive"));
const ProcessingJobs = React.lazy(() => import("./pages/toolbox/aurum/ProcessingJobs"));
const HandsFreePage = React.lazy(() => import("./pages/hands-free/HandsFreePage"));

// Agora (marketplace) — lazily loaded to keep them out of the main bundle
const Agora = React.lazy(() => import("@/pages/agora/professionals/Marketplace"));
const AgoraLanding = React.lazy(() => import("@/pages/agora/AgoraLanding"));
const ProductsLanding = React.lazy(() => import("@/pages/agora/products/ProductsLanding"));
const ProductsSetup = React.lazy(() => import("@/pages/agora/products/ProductsSetup"));
const ProductsPlan = React.lazy(() => import("@/pages/agora/products/ProductsPlan"));
const ProductsOrder = React.lazy(() => import("@/pages/agora/products/ProductsOrder"));
const ProductsOrders = React.lazy(() => import("@/pages/agora/products/ProductsOrders"));
const PeopleLanding = React.lazy(() => import("@/pages/agora/people/PeopleLanding"));
const NewConsultant = React.lazy(() => import("@/pages/agora/professionals/NewConsultant"));
const ConsultantDashboard = React.lazy(() => import("@/pages/agora/professionals/ConsultantDashboard"));
const ConsultantPending = React.lazy(() => import("@/pages/agora/professionals/ConsultantPending"));
const ConsultantProfileEdit = React.lazy(() => import("@/pages/agora/professionals/ConsultantProfileEdit"));
const ConsultantCertifications = React.lazy(() => import("@/pages/agora/professionals/ConsultantCertifications"));
const ConsultantPublicProfile = React.lazy(() => import("@/pages/agora/professionals/ConsultantPublicProfile"));
const BookConsultation = React.lazy(() => import("@/pages/agora/professionals/BookConsultation"));
const ClientConsultations = React.lazy(() => import("@/pages/agora/professionals/ClientConsultations"));
const ConsultationDetail = React.lazy(() => import("@/pages/agora/professionals/ConsultationDetail"));
const AdminDashboard = React.lazy(() => import("@/pages/agora/professionals/admin/AdminDashboard"));
const VerificationDetail = React.lazy(() => import("@/pages/agora/professionals/admin/VerificationDetail"));

// Create a client
const queryClient = new QueryClient();

// Simple NotFound component — lazy loaded from pages
const NotFound = React.lazy(() => import("./pages/NotFound"));

function LegacyAbacusRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function LegacyQualityAssessmentRedirect() {
  const { batchId } = useParams();
  const { search } = useLocation();
  if (batchId) {
    return <Navigate to={`/abacus/quality/batch/${batchId}${search}`} replace />;
  }
  return <Navigate to={`/abacus/quality/batch${search}`} replace />;
}

function LegacyQualityBatchRedirect() {
  const { batchId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/abacus/quality/batch/${batchId}${search}`} replace />;
}

function App() {
  return (
    <ThemeProvider>
    <TooltipProvider>
      <Router>
        <GlobalGestureHandler>
          <AppLayout>
            <AuthProvider>
              <FacilityProvider>
              <QueryClientProvider client={queryClient}>
                <NotificationsProvider>
                  <Suspense fallback={<SuspenseEmberLoader />}>
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route
                        path="/signup"
                        element={isWaitlistCurtainEnabled() ? <Navigate to="/" replace /> : <SignUp />}
                      />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route
                        path="/"
                        element={isWaitlistCurtainEnabled() ? <WaitlistCurtain /> : <Index />}
                      />
                      <Route
                        path="/solutions"
                        element={isWaitlistCurtainEnabled() ? <Navigate to="/" replace /> : <Solutions />}
                      />
                      <Route
                        path="/pricing"
                        element={isWaitlistCurtainEnabled() ? <Navigate to="/" replace /> : <Pricing />}
                      />
                      <Route
                        path="/about"
                        element={isWaitlistCurtainEnabled() ? <Navigate to="/" replace /> : <About />}
                      />
                      <Route
                        path="/contact"
                        element={isWaitlistCurtainEnabled() ? <Navigate to="/" replace /> : <Contact />}
                      />
                      <Route path="/legal/privacy" element={<LegalPrivacy />} />
                      <Route path="/legal/terms" element={<LegalTerms />} />
                      <Route path="/join/:inviteCode" element={<JoinFacility />} />
                      <Route path="/welcome" element={
                        <ProtectedRoute>
                          <Welcome />
                        </ProtectedRoute>
                      } />
                      
                      {/* Protected Routes — app shell */}
                      <Route element={
                        <ProtectedRoute>
                          <DashboardShell />
                        </ProtectedRoute>
                      }>
                      <Route path="/dashboard" element={<DashboardHome />} />
                      <Route path="/getting-started" element={<GettingStarted />} />
                      <Route path="/help" element={<Help />} />
                      <Route path="/admin/tutorials" element={<AdminRoute><AdminTutorials /></AdminRoute>} />
                      <Route path="/admin/teach-prometheus" element={<AdminRoute><TeachPrometheus /></AdminRoute>} />
                      <Route path="/profile" element={
                        <Profile />
                      } />
                      <Route path="/profile/facilities" element={
                        <FacilitiesManager />
                      } />
                      <Route path="/facility/create" element={
                        <CreateFacility />
                      } />
                      <Route path="/facility/:facilityId/settings" element={
                        <FacilitySettings />
                      } />
                      <Route path="/profile/integrations" element={
                        <IntegrationsPage />
                      } />
                      <Route path="/subscription/success" element={
                        <SubscriptionSuccess />
                      } />
                      <Route path="/clients" element={<Outlet />}>
                        <Route index element={<ClientsOrgSelect />} />
                        <Route path="not-authorized" element={<NotAuthorized />} />
                        <Route path=":orgSlug" element={<ClientsPortalIndex />} />
                        <Route path=":orgSlug/resources" element={<ClientsResources />} />
                        <Route path=":orgSlug/education" element={<ClientsEducation />} />
                        <Route path=":orgSlug/communication" element={<ClientsCommunication />} />
                        <Route path=":orgSlug/workspace" element={<ClientsWorkspace />} />
                        <Route path=":orgSlug/*" element={<NotAuthorized />} />
                      </Route>
                      <Route path="/arca" element={
                        <ArcaLanding />
                      } />
                      <Route path="/aurum" element={
                        <AdminRouteGuard>
                          <AurumLanding />
                        </AdminRouteGuard>
                      } />
                      <Route path="/aurum/logs" element={
                        <AdminRouteGuard>
                          <LogsEngine />
                        </AdminRouteGuard>
                      } />
                      <Route path="/aurum/sops" element={
                        <AdminRouteGuard>
                          <SOPVault />
                        </AdminRouteGuard>
                      } />
                      <Route path="/aurum/sds" element={
                        <AdminRouteGuard>
                          <SDSInventory />
                        </AdminRouteGuard>
                      } />
                      <Route path="/aurum/purchases" element={
                        <AdminRouteGuard>
                          <PurchaseArchive />
                        </AdminRouteGuard>
                      } />
                      <Route path="/aurum/processing" element={
                        <AdminRouteGuard>
                          <ProcessingJobs />
                        </AdminRouteGuard>
                      } />
                      <Route path="/hands-free" element={
                        <HandsFreePage />
                      } />
                      <Route path="/atlas/*" element={
                        <Atlas />
                      } />
                      <Route path="/animus" element={
                        <AnimusLanding />
                      } />
                      {/* Crops — canonical source of truth (Phase 6). Core, all tiers. */}
                      <Route path="/crops" element={
                        <CropsLanding />
                      } />
                      <Route path="/crops/standup" element={
                        <Standup />
                      } />
                      <Route path="/crops/my-tasks" element={
                        <MyTasks />
                      } />
                      <Route path="/crops/spaces" element={
                        <SpaceTimeline />
                      } />
                      <Route path="/crops/:id" element={
                        <CropDetail />
                      } />
                      <Route path="/arca/diary" element={
                        <CultivationDiary />
                      } />
                      <Route path="/arca/sds" element={
                        <ArcaSdsHub />
                      } />
                      <Route path="/arca/sds/identifier" element={
                        <Navigate to="/arca/sds" replace />
                      } />
                      <Route path="/arca/sds/catalog" element={
                        <Navigate to="/arca/sds" replace />
                      } />
                      <Route path="/arca/sds/review" element={
                        <ArcaCatalogReview />
                      } />
                      {/* Prometheus - the single agentic surface (T-303). The old
                          service-role PrometheusBeta / supervisor-agent-beta path was
                          retired in T-305 (cross-tenant RLS risk). */}
                      <Route path="/prometheus" element={
                        <PrometheusAgent />
                      } />
                      {/* Legacy Prometheus routes - hidden but kept for backward compatibility */}
                      {/* <Route path="/prometheus2" element={
                        <Prometheus2 />
                      } />
                      <Route path="/prometheus3" element={
                        <Prometheus3 />
                      } />
                      <Route path="/prometheus4" element={
                        <Prometheus4 />
                      } /> */}
                      <Route path="/animus/library" element={
                        <StrainLibrary />
                      } />
                      <Route path="/animus/analytics" element={
                        <AnimusAnalytics />
                      } />
                      <Route path="/animus/archive" element={<StrainArchive />} />
                      <Route path="/arca/calculator" element={
                        <ShotSizeCalculator />
                      } />
                      <Route path="/arca/converter" element={
                        <UnitConverter />
                      } />
                      <Route path="/arca/chemical" element={
                        <ChemicalCalculator />
                      } />
                      <Route path="/arca/photothermal" element={
                        <PhotothermalRatioCalculator />
                      } />
                      <Route path="/arca/light-lab" element={
                        <LightLabLayout />
                      }>
                        <Route index element={<Navigate to="dli-core" replace />} />
                        <Route path="intensity" element={<Navigate to="dli-core" replace />} />
                        <Route path="dli-core" element={<LightLabDliCoreTool />} />
                        <Route path="ramp" element={<LightLabRampTool />} />
                        <Route path="spectrum" element={<LightLabSpectrumTool />} />
                        <Route
                          path="spectrum-blend"
                          element={<Navigate to="/arca/light-lab/spectrum" replace />}
                        />
                        <Route path="energy" element={<LightLabEnergyTool />} />
                      </Route>
                      <Route path="/arca/runofflogger" element={
                        <Navigate to="/arca/runofflogger/logger" replace />
                      } />
                      <Route path="/arca/runofflogger/logger" element={
                        <RunoffManager initialTab="log" />
                      } />
                      <Route path="/arca/runofflogger/records" element={
                        <RunoffManager initialTab="records" />
                      } />
                      <Route path="/arca/agrochemlog" element={
                        <AgroChemLog />
                      } />
                      <Route path="/arca/application-log" element={
                        <Navigate to="/arca/agrochemlog" replace />
                      } />
                      {/* New Beneficial Insects Route */}
                      <Route path="/arca/ipm" element={
                        <BeneficialInsects />
                      } />
                      {/* Other Arca Tools Routes */}
                      <Route path="/arca/targets" element={
                        <Targets />
                      } />
                      <Route path="/arca/tanks" element={<Tanks />}>
                        <Route index element={<Navigate to="stock" replace />} />
                        <Route path="stock" element={<StockTanks />} />
                        <Route path="batch" element={<BatchTanks />} />
                        <Route path="batch/add" element={<CreateBatchTank />} />
                        <Route path="batch/edit/:id" element={<EditBatchTank />} />
                        <Route path="fertilizers" element={<Fertilizers />} />
                      </Route>
                      <Route path="/arca/fertilizers" element={<Navigate to="/arca/tanks/fertilizers" replace />} />
                      <Route path="/arca/fertility" element={
                        <Fertility />
                      } />
                      <Route path="/arca/fertility-application-log" element={
                        <FertilityApplicationLog />
                      } />
                      {/* Aura – live environmental + substrate dashboard */}
                      <Route path="/aura" element={
                        <AroyaReadings />
                      } />
                      <Route path="/aura/readings" element={
                        <AroyaReadings />
                      } />
                      {/* Legacy /aroya links redirect to /aura */}
                      <Route path="/aroya" element={<Navigate to="/aura" replace />} />
                      <Route path="/aroya/readings" element={<Navigate to="/aura/readings" replace />} />
                      {/* Abacus – harvest yield, quality, economics */}
                      <Route path="/abacus" element={
                        <AbacusLanding />
                      } />
                      <Route path="/abacus/logger" element={
                        <YieldManager initialTab="log" />
                      } />
                      <Route path="/abacus/records" element={
                        <YieldManager initialTab="records" />
                      } />
                      <Route path="/abacus/prices" element={
                        <PriceSettings />
                      } />
                      <Route path="/abacus/analytics" element={
                        <YieldAnalytics />
                      } />
                      <Route path="/abacus/quality" element={
                        <Navigate to="/abacus/quality/batch" replace />
                      } />
                      <Route path="/abacus/quality/batch" element={
                        <QualityAssessmentPage initialTab="batch" />
                      } />
                      <Route path="/abacus/quality/batch/:batchId" element={
                        <QualityAssessmentPage initialTab="batch" />
                      } />
                      <Route path="/abacus/quality/history" element={
                        <QualityAssessmentPage initialTab="history" />
                      } />
                      <Route path="/abacus/quality/trends" element={
                        <QualityAssessmentPage initialTab="trends" />
                      } />
                      <Route path="/abacus/quality/weightings" element={
                        <QualityWeightingsSettingsPage />
                      } />
                      {/* Phase 19 · GV-7 — the facility's own yield vocabulary */}
                      <Route path="/abacus/settings/grades" element={
                        <YieldGradesSettingsPage />
                      } />
                      <Route path="/abacus/quality/:batchId" element={
                        <LegacyQualityBatchRedirect />
                      } />

                      {/* Legacy Arca yield URLs → Abacus */}
                      <Route path="/arca/yields" element={
                        <LegacyAbacusRedirect to="/abacus" />
                      } />
                      <Route path="/arca/yields/logger" element={
                        <LegacyAbacusRedirect to="/abacus/logger" />
                      } />
                      <Route path="/arca/yields/records" element={
                        <LegacyAbacusRedirect to="/abacus/records" />
                      } />
                      <Route path="/arca/yields/prices" element={
                        <LegacyAbacusRedirect to="/abacus/prices" />
                      } />
                      <Route path="/arca/yields/analytics" element={
                        <LegacyAbacusRedirect to="/abacus/analytics" />
                      } />
                      <Route path="/arca/quality" element={
                        <LegacyQualityAssessmentRedirect />
                      } />
                      <Route path="/arca/quality/:batchId" element={
                        <LegacyQualityAssessmentRedirect />
                      } />

                      {/* Add future Arca tool routes here */}
                      <Route path="/arca/irrigation-forecaster" element={
                        <IrrigationSimulator />
                      } />
                      <Route path="/arca/ppms" element={
                        <PPMAnalysis />
                      } />
                      <Route path="/arca/ppms/logger" element={
                        <PPMAnalysisLogger />
                      } />
                      <Route path="/arca/ppms/records" element={
                        <PPMAnalysisRecords />
                      } />
                      <Route path="/arca/filters" element={
                        <Navigate to="/arca/filters/logger" replace />
                      } />
                      <Route path="/arca/filters/logger" element={
                        <FilterManager initialTab="logger" />
                      } />
                      <Route path="/arca/filters/records" element={
                        <FilterManager initialTab="records" />
                      } />
                      <Route path="/arca/filters/calendar" element={
                        <FilterManager initialTab="calendar" />
                      } />
                      <Route path="/arca/*" element={
                        <Navigate to="/arca" replace />
                      } />
                      {/* Animus module routes */}
                      <Route path="/animus/origins" element={
                        <Origins />
                      } />
                      
                      {/* Breeding Project Detail Route - Displays individual breeding project information */}
                      <Route path="/animus/origins/:projectId" element={
                        <BreedingProjectDetail />
                      } />
                      
                      {/* Selection Management Route - Add our new SelectionsPage */}
                      <Route path="/animus/origins/:projectId/selections" element={
                        <SelectionsPage />
                      } />
                      
                      {/* Add the Traits route here */}
                      <Route path="/animus/traits" element={
                        <Traits />
                      } />
                      
                      {/* Add the Trait Project Detail route */}
                      <Route path="/animus/traits/:projectId" element={
                        <TraitProjectDetail />
                      } />
                      
                      {/* Add catch-all for Animus routes */}
                      <Route path="/animus/*" element={
                        <Navigate to="/animus" replace />
                      } />
                      <Route path="/feedback" element={<Feedback />} />
                      <Route path="/spark" element={
                        <ProtectedRoute>
                          <SparkDashboard />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin" element={
                        <AdminRoute>
                          <AdminHome />
                        </AdminRoute>
                      } />
                      <Route path="/admin/ops" element={
                        <AdminRoute>
                          <AdminOpsOverview />
                        </AdminRoute>
                      } />
                      <Route path="/admin/ops/accounts" element={
                        <AdminRoute>
                          <AdminOpsAccounts />
                        </AdminRoute>
                      } />
                      <Route path="/admin/ops/accounts/:facilityId" element={
                        <AdminRoute>
                          <AdminOpsAccountDetail />
                        </AdminRoute>
                      } />
                      <Route path="/admin/ops/users" element={
                        <AdminRoute>
                          <AdminOpsUsers />
                        </AdminRoute>
                      } />
                      <Route path="/admin/ops/lifecycle" element={
                        <AdminRoute>
                          <AdminOpsLifecycle />
                        </AdminRoute>
                      } />
                      <Route path="/admin/ops/revenue" element={
                        <AdminRoute>
                          <AdminOpsRevenue />
                        </AdminRoute>
                      } />
                      <Route path="/admin/ops/health" element={
                        <AdminRoute>
                          <AdminOpsHealth />
                        </AdminRoute>
                      } />
                      <Route path="/admin/ops/audit" element={
                        <AdminRoute>
                          <AdminOpsAudit />
                        </AdminRoute>
                      } />
                      <Route path="/admin/ops/spark" element={
                        <AdminRoute>
                          <AdminOpsSpark />
                        </AdminRoute>
                      } />
                      <Route path="/admin/feedback" element={
                        <AdminRoute>
                          <AdminFeedback />
                        </AdminRoute>
                      } />
                      <Route path="/admin/user-feedback" element={
                        <AdminRoute>
                          <UserFeedbackDashboard />
                        </AdminRoute>
                      } />
                      <Route path="/admin/plus-advising" element={
                        <AdminRoute>
                          <PlusAdvisingConsole />
                        </AdminRoute>
                      } />
                      <Route path="/admin/insights" element={
                        <AdminRoute>
                          <AdminInsights />
                        </AdminRoute>
                      } />
                      <Route path="/admin/insights/forecasts" element={
                        <AdminRoute>
                          <AdminForecasts />
                        </AdminRoute>
                      } />
                      <Route path="/admin/insights/assumptions" element={
                        <AdminRoute>
                          <AdminAssumptions />
                        </AdminRoute>
                      } />
                      <Route path="/admin/growgeneration/leads" element={<AdminRoute><AdminGrowgenLeads /></AdminRoute>} />
                      <Route path="/admin/growgeneration" element={
                        <AdminRoute>
                          <AdminGrowGen />
                        </AdminRoute>
                      } />
                      <Route path="/admin/blessings" element={
                        <AdminRoute>
                          <AdminBlessings />
                        </AdminRoute>
                      } />
                      <Route path="/admin/usage" element={
                        <AdminRoute>
                          <AdminUsage />
                        </AdminRoute>
                      } />
                      <Route path="/admin/ancora" element={
                        <AdminRoute><AncoraStudio /></AdminRoute>
                      } />
                      <Route path="/admin/waitlist" element={
                        <AdminRoute>
                          <AdminWaitlist />
                        </AdminRoute>
                      } />
                      <Route path="/facility/admin" element={<FacilityAdministration />} />
                      <Route path="/agora" element={<AgoraLanding />} />
                      <Route path="/agora/products" element={<ProductsLanding />} />
                      <Route path="/agora/products/setup" element={<ProductsSetup />} />
                      <Route path="/agora/products/plan" element={<ProductsPlan />} />
                      <Route path="/agora/products/order" element={<ProductsOrder />} />
                      <Route path="/agora/products/orders" element={<ProductsOrders />} />
                      <Route path="/agora/people" element={<PeopleLanding />} />
                      <Route path="/agora/professionals" element={
                        <AgoraAuthProvider>
                          <Agora />
                        </AgoraAuthProvider>
                      } />
                      <Route path="/agora/professionals/consultants/new" element={
                        <AgoraAuthProvider>
                            <NewConsultant />
                          </AgoraAuthProvider>
                      } />
                      <Route path="/agora/professionals/consultants/pending" element={
                        <ConsultantPending />
                      } />
                      <Route path="/agora/professionals/consultants/dashboard" element={
                        <AgoraAuthProvider>
                            <ConsultantDashboard />
                          </AgoraAuthProvider>
                      } />
                      <Route path="/agora/professionals/consultants/profile" element={
                        <AgoraAuthProvider>
                            <ConsultantProfileEdit />
                          </AgoraAuthProvider>
                      } />
                      <Route path="/agora/professionals/consultants/certifications" element={
                        <AgoraAuthProvider>
                            <ConsultantCertifications />
                          </AgoraAuthProvider>
                      } />
                      <Route path="/agora/professionals/consultants/:consultantId" element={
                        <AgoraAuthProvider>
                          <ConsultantPublicProfile />
                        </AgoraAuthProvider>
                      } />
                      <Route path="/agora/professionals/book/:consultantId" element={
                        <AgoraAuthProvider>
                          <BookConsultation />
                        </AgoraAuthProvider>
                      } />
                      <Route path="/agora/professionals/my-consultations" element={
                        <AgoraAuthProvider>
                            <ClientConsultations />
                          </AgoraAuthProvider>
                      } />
                      <Route path="/agora/professionals/consultations/:consultationId" element={
                        <AgoraAuthProvider>
                            <ConsultationDetail />
                          </AgoraAuthProvider>
                      } />
                      
                      {/* Agora Admin Routes */}
                      <Route path="/agora/professionals/admin" element={
                        <AdminRouteGuard>
                          <AdminDashboard />
                        </AdminRouteGuard>
                      } />
                      <Route path="/agora/professionals/admin/verification/:id" element={
                        <AdminRouteGuard>
                          <VerificationDetail />
                        </AdminRouteGuard>
                      } />

                      {/* Legacy consulting paths (pre-partition) → /agora/professionals/* */}
                      <Route path="/agora/consultants/*" element={<LegacyAgoraRedirect />} />
                      <Route path="/agora/book/*" element={<LegacyAgoraRedirect />} />
                      <Route path="/agora/my-consultations" element={<LegacyAgoraRedirect />} />
                      <Route path="/agora/consultations/*" element={<LegacyAgoraRedirect />} />
                      <Route path="/agora/admin/*" element={<LegacyAgoraRedirect />} />

                      {/* Ancora - Online Academy Routes */}
                      <Route path="/ancora" element={
                        <AncoraLanding />
                      } />
                      <Route path="/ancora/courses" element={
                        <AncoraCourses />
                      } />
                      <Route path="/ancora/pro" element={
                        <AncoraPro />
                      } />
                      <Route path="/ancora/atoms/:atomSlug" element={
                        <AncoraAtomView />
                      } />
                      <Route path="/ancora/themes/:themeSlug" element={
                        <AncoraThemeLibrary />
                      } />
                      {/* New Ancora Academy Routes */}
                      <Route path="/ancora/courses/:courseId" element={
                        <CourseView />
                      } />
                      <Route path="/ancora/courses/:courseId/lessons/:lessonId" element={
                        <LessonPlayer />
                      } />
                      <Route path="/ancora/courses/:courseId/modules/:moduleId" element={
                        <LessonPlayer />
                      } />
                      <Route path="/ancora/courses/:courseId/lessons/:lessonId/quiz" element={
                        <QuizPlayer />
                      } />
                      
                      {/* Test route for the quiz component */}
                      <Route path="/ancora/test/quiz/:lessonId" element={
                        <QuizComponentTest />
                      } />
                      
                      <Route path="/ancora/courses/:courseId/quiz/:quizId" element={
                        <div className="w-full h-full">
                          <QuizComponent
                            lessonId="placeholder"
                            onComplete={(score, totalQuestions, criticalThinkingSubmitted) => {
                              console.log("Quiz completed", { score, totalQuestions, criticalThinkingSubmitted });
                            }}
                          />
                        </div>
                      } />
                      <Route path="/ancora/certifications/:courseId" element={
                        <div className="w-full h-full">
                          <CertificationAward courseId="placeholder" courseTitle="Course" />
                        </div>
                      } />
                      <Route path="/ancora/certifications" element={
                        <div className="container mx-auto py-8">
                            <h1 className="text-2xl font-bold mb-4">Your Certifications</h1>
                            <p>View and manage your earned course certifications.</p>
                          </div>
                      } />
                      <Route path="/ancora/recruiting-tool" element={
                        <ResumeBuilder />
                      } />
                      <Route path="/ancora/*" element={
                        <Navigate to="/ancora" replace />
                      } />
                      {/* Admin Routes */}
                      <Route path="/admin/quizzes/new" element={
                        <AdminRoute>
                          <QuizBuilder />
                        </AdminRoute>
                      } />
                      <Route path="/admin/quizzes/edit/:lessonId" element={
                        <AdminRoute>
                          <QuizBuilder />
                        </AdminRoute>
                      } />
                      </Route>

                      {/* Catch-all route */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                  <FeedbackWidget />
                  <FloatingAssistant />
                  <Toaster richColors position="top-center" />
                </NotificationsProvider>
              </QueryClientProvider>
              </FacilityProvider>
            </AuthProvider>
          </AppLayout>
        </GlobalGestureHandler>
      </Router>
    </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
